import { DatabaseApiKeyService } from '@qdrant/qdrant-cloud-public-api/cluster/auth/v1/database_api_key';
import { createFileRoute, lazyRouteComponent } from '@tanstack/react-router';
import { z } from 'zod';
import { assertRpcPermissions } from '@/api/permissions';
import { databaseApiKeyV1Service } from '@/api/services/authentication';
import { databaseApiKeyV2Service } from '@/api/services/database-api-key';
import { MIN_CLUSTER_VERSION_FOR_RBAC } from '@/components/Access/RBAC/utils';
import { hasClusterRbacEnabled } from '@/utils/cluster-utils';
import { isAtLeastVersion } from '@/utils/helpers';

export const Route = createFileRoute(
  '/_authenticated/_dashboard/accounts/$accountId/clusters/$clusterId/_tabs/api-keys',
)({
  validateSearch: z.object({
    apiKeyDialog: z.coerce.boolean().nullish(),
  }),
  beforeLoad: async ({ context: { queryClient, clusterQuery }, params: { accountId, clusterId } }) => {
    // The gate: refuses with an AccessControlError (the Access Denied screen) when the account may not
    // `read:api_keys`. Synchronous, from the permissions `$accountId` cached, so the tab never waits on it.
    assertRpcPermissions([DatabaseApiKeyService.method.listDatabaseApiKeys], { queryClient, accountId });

    const clusterResponse = await queryClient.fetchQuery(clusterQuery);
    const cluster = clusterResponse.cluster;
    const isClusterRbacEnabled = hasClusterRbacEnabled(cluster);
    const clusterIsCompatibleWithRbac = isAtLeastVersion(MIN_CLUSTER_VERSION_FOR_RBAC, cluster.configuration?.version);
    const jwtsEnabled = isClusterRbacEnabled && clusterIsCompatibleWithRbac;

    return {
      listDatabaseApiKeysV1Query: databaseApiKeyV1Service.listDatabaseApiKeysQuery({ accountId, clusterId }),
      clusterJwtQuery: jwtsEnabled ? databaseApiKeyV2Service.listDatabaseApiKeysQuery({ accountId, clusterId }) : null,
    };
  },
  // Not awaited: the tab renders and its lists suspend behind their own skeletons. `prefetchQuery`, not
  // `fetchQuery`, because nothing awaits these and only `prefetchQuery` never rejects.
  loader: ({ context: { queryClient, clusterJwtQuery, listDatabaseApiKeysV1Query } }) => {
    if (clusterJwtQuery) {
      void queryClient.prefetchQuery(clusterJwtQuery);
    }

    void queryClient.prefetchQuery(listDatabaseApiKeysV1Query);
  },
  component: lazyRouteComponent(() => import('./api-keys.component'), 'ApiKeysComponent'),
  pendingComponent: lazyRouteComponent(() => import('./api-keys.component'), 'ApiKeysPendingComponent'),
});
